import React from 'react';
import { InputNumber, Space, Typography } from 'antd';
import { formatKg } from '@/services/engineeringMaterial.service';

const { Text } = Typography;

/** Theoretical weight of the received part of a PO line (pro rata on ordered quantity). */
export function expectedReceiptWeight(
  theoreticalWeight: unknown,
  receivedQty: unknown,
  orderedQty: unknown
): number | null {
  const w = Number(theoreticalWeight);
  const ordered = Number(orderedQty);
  if (theoreticalWeight == null || theoreticalWeight === '' || !Number.isFinite(w) || !(ordered > 0)) return null;
  return (w * (Number(receivedQty) || 0)) / ordered;
}

/** PO line theoretical weight for a GRN line: receivable meta first, else the joined PO line. */
export function lineTheoreticalWeight(
  line: { purchaseOrderItem?: { theoretical_weight?: number | string | null } },
  meta?: { theoretical_weight?: number | null }
): number | null {
  const raw = meta?.theoretical_weight ?? line.purchaseOrderItem?.theoretical_weight;
  return raw == null || raw === '' || !Number.isFinite(Number(raw)) ? null : Number(raw);
}

interface VarianceProps {
  expected: number | null;
  actual: number | null | undefined;
  tolerancePlusPct?: number | null;
  toleranceMinusPct?: number | null;
}

/** Actual vs theoretical weight. Information only: never blocks or changes posting. */
export const GrnWeightVariance: React.FC<VarianceProps> = ({ expected, actual, tolerancePlusPct, toleranceMinusPct }) => {
  if (expected == null) return null;
  if (actual == null || !(expected > 0)) {
    return (
      <Text type="secondary" style={{ fontSize: 11 }}>
        Theoretical {formatKg(expected)}
      </Text>
    );
  }
  const pct = ((actual - expected) / expected) * 100;
  const hasTolerance = tolerancePlusPct != null || toleranceMinusPct != null;
  const outside = hasTolerance && (pct > Number(tolerancePlusPct ?? Infinity) || -pct > Number(toleranceMinusPct ?? Infinity));
  return (
    <Text type={outside ? 'warning' : 'secondary'} style={{ fontSize: 11 }}>
      Theoretical {formatKg(expected)} · {pct >= 0 ? '+' : ''}
      {pct.toFixed(1)}%{outside ? ' — outside tolerance' : ''}
    </Text>
  );
};

interface InputProps extends VarianceProps {
  onChange: (value: number | null) => void;
}

export const GrnActualWeightInput: React.FC<InputProps> = ({ onChange, ...variance }) => (
  <Space direction="vertical" size={0}>
    <InputNumber
      size="small"
      min={0}
      placeholder="kg"
      value={variance.actual ?? undefined}
      onChange={(v) => onChange(v == null ? null : Number(v))}
      style={{ width: 100 }}
    />
    <GrnWeightVariance {...variance} />
  </Space>
);
